import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getUsersById } from 'auth/store/users/selectors';
import Flex from 'base/components/Flex';
import { CloseIcon, ArrowForwardIcon } from 'base/components/Icon';
import CustomScroll from 'base/components/ScrollBar';
import { navigateToDocument } from 'documents/store/documents/actions';
import Loader from 'base/components/Loader';
import FileExplorerTeaser from 'dashboard/containers/FileExplorerTeaser/FileExplorerTeaser';
import FileExplorerTeaserSkeleton from 'dashboard/containers/FileExplorerTeaser/FileExplorerTeaserSkeleton';
import { getAuthUser } from 'auth/store/auth/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { navigateToFolder } from 'documents';
import { getDefaultFavorites } from '../../store/defaultFavorites/selectors';
import { removeDefaultFavorite, fetchDefaultFavorites } from '../../store/defaultFavorites/actions';

const WidgetWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  height: 100%;
`;

export default function DefaultFavorites({ isAdminMode, dashboardId, isLoading }) {
  const dispatch = useAppDispatch();
  const defaultFavorites = useAppSelector(getDefaultFavorites);
  const users = useAppSelector(getUsersById);
  const myUser = useAppSelector(getAuthUser);

  const fetch = useCallback(() => {
    if (isAdminMode) {
      dispatch(fetchDefaultFavorites(dashboardId));
    } else if (myUser) {
      // TODO: sometimes defaultDashboard is set as an id, sometimes as an object
      // Dirty workaround for demo, but fix this later
      const defaultDashboardId =
        myUser.defaultDashboard && myUser.defaultDashboard.id ? myUser.defaultDashboard.id : myUser.defaultDashboard;
      dispatch(fetchDefaultFavorites(defaultDashboardId));
    }
  }, [dashboardId, dispatch, isAdminMode, myUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [dashboardId, fetch, myUser?.id]);

  const handleClick = useCallback(
    entity => {
      if (entity.entityType === 'document') {
        dispatch(navigateToDocument(entity));
      } else {
        dispatch(navigateToFolder(entity));
      }
    },
    [dispatch],
  );

  const handleRemove = useCallback(
    entity => {
      dispatch(removeDefaultFavorite(dashboardId, entity.entityType, entity.id));
    },
    [dashboardId, dispatch],
  );

  const actions = [
    {
      icon: ArrowForwardIcon,
      onClick: handleClick,
      key: 'primary',
    },
  ];
  if (isAdminMode) {
    actions.push({
      icon: CloseIcon,
      onClick: handleRemove,
      key: 'remove',
    });
  }

  return (
    <WidgetWrapper>
      <CustomScroll>
        <Loader isLoading={isLoading} fallback={<FileExplorerTeaserSkeleton />}>
          {defaultFavorites &&
            defaultFavorites.map(document => (
              <FileExplorerTeaser key={document.id} entity={document} users={users} actions={actions} />
            ))}
        </Loader>
      </CustomScroll>
    </WidgetWrapper>
  );
}
DefaultFavorites.propTypes = {
  isAdminMode: PropTypes.bool,
  dashboardId: PropTypes.number,
  isLoading: PropTypes.bool,
};
DefaultFavorites.defaultProps = {
  isAdminMode: false,
  dashboardId: null,
  isLoading: false,
};
